import Phaser from '../lib/phaser.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { BaseScene } from './base-scene.js';
import { SCENE_KEYS } from './scene-keys.js';
import { netManager } from '../net/index.js';
import { UI, makeButton, makeCloseButton } from '../utils/ui-button.js';
import {
  fetchFriends,
  sendFriendRequest,
  acceptFriend,
  removeFriend as apiRemoveFriend,
  getPublicProfile,
  searchPlayers,
} from '../net/api.js';

// ── Friends & Invites — social panel (mouse/touch-driven overlay). ──
// Mirrors leaderboard-scene / card-shop-scene styling (dark glass, emerald/gold, corner accents)
// and their input contract (`_claimOverlayInput()` + a circular close button + ESC). Shows:
//   • my friends, each with a green/grey online dot + username (+ a small ✕ to unfriend)
//   • incoming requests with an Accept button
//   • an "Add friend" username/wallet input + Add button (single DOM <input>, cleaned up on close)
//   • a prominent "Invite Friends" button that copies my invite link to the clipboard + toasts
// Everything degrades gracefully offline / walletless (calm empty state, never crashes).

const ONLINE = 0x34d399;
const OFFLINE_DOT = 0x55685e;

/**
 * @typedef FriendsSceneData
 * @type {object}
 * @property {string} [previousSceneName]
 */

export class FriendsScene extends BaseScene {
  /** @type {FriendsSceneData} */ #sceneData;
  /** @type {import('../net/api.js').FriendsView} */ #view = { friends: [], incoming: [] };
  /** @type {Phaser.GameObjects.Container} */ #rowsContainer;
  /** @type {Phaser.GameObjects.Text} */ #statusText;
  /** @type {number} */ #panelLeft = 0;
  /** @type {number} */ #panelW = 0;
  /** @type {number} */ #rowsTop = 0;
  /** @type {number} */ #rowsBottom = 0;
  /** @type {boolean} */ #busy = false;
  /** @type {HTMLInputElement | undefined} */ #addInput;
  /** @type {Phaser.GameObjects.Container | undefined} */ #profileContainer;
  /** @type {HTMLDivElement | undefined} */ #suggestBox;
  /** @type {ReturnType<typeof setTimeout> | undefined} */ #suggestTimer;
  /** @type {HTMLElement[]} */ #domEls = [];

  constructor() {
    super({ key: SCENE_KEYS.FRIENDS_SCENE });
  }

  /** @param {FriendsSceneData} data */
  init(data) {
    super.init(data);
    this.#sceneData = data || {};
  }

  create() {
    super.create();
    this._claimOverlayInput();
    const { width, height } = this.scale;

    this.add.rectangle(0, 0, width, height, 0x05080d, 0.82).setOrigin(0);

    const panelW = Math.min(720, width - 60);
    const panelH = Math.min(640, height - 40);
    const cx = width / 2;
    const top = height / 2 - panelH / 2;
    const left = cx - panelW / 2;
    this.#panelLeft = left;
    this.#panelW = panelW;

    const panel = this.add.graphics();
    panel.fillStyle(UI.glass, 0.96);
    panel.fillRoundedRect(left, top, panelW, panelH, 18);
    panel.lineStyle(2, UI.emerald, 0.6);
    panel.strokeRoundedRect(left, top, panelW, panelH, 18);

    this.add
      .text(cx, top + 40, 'FRIENDS', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '40px',
        color: UI.emeraldHex,
        stroke: '#05351f',
        strokeThickness: 6,
      })
      .setOrigin(0.5);

    this.add
      .text(cx, top + 72, '👥  YOUR CREW', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '14px',
        color: UI.goldHex,
      })
      .setOrigin(0.5);

    makeCloseButton(this, left + panelW - 34, top + 34, () => this.#close());

    // ── "Add friend" row: DOM input (username OR wallet) + an Add button. ──
    const addY = top + 108;
    this.#buildAddInput(left + 30, addY, panelW - 60 - 110);
    makeButton(this, {
      x: left + panelW - 30 - 50,
      y: addY + 22,
      width: 100,
      height: 44,
      label: 'Add',
      primary: true,
      fontSize: 18,
      onClick: () => void this.#add(),
    });

    // ── Invite Friends (copies my link to the clipboard). ──
    const inviteY = addY + 70;
    makeButton(this, {
      x: cx,
      y: inviteY,
      width: panelW - 60,
      height: 50,
      label: '🔗  Invite Friends — copy my link',
      fontSize: 20,
      onClick: () => this.#copyInvite(),
    });

    // ── Scrollable list region (friends + incoming). ──
    this.#rowsTop = inviteY + 50;
    this.#rowsBottom = top + panelH - 24;
    this.#rowsContainer = this.add.container(0, 0);

    this.#statusText = this.add
      .text(cx, (this.#rowsTop + this.#rowsBottom) / 2, 'Loading friends…', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '16px',
        color: UI.subHex,
      })
      .setOrigin(0.5);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.#destroyDom());
    void this.#load();
    this.cameras.main.fadeIn(160, 0, 0, 0);
  }

  /** The DOM <input> for adding a friend by username or wallet (positioned over the panel). */
  #buildAddInput(x, y, w) {
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Username or wallet…';
    input.maxLength = 64;
    this.#positionDom(input, x, y, w, 44);
    input.style.cssText += [
      'box-sizing:border-box',
      'padding:11px 14px',
      'border-radius:12px',
      'border:1px solid rgba(52,211,153,0.4)',
      'background:rgba(8,18,12,0.9)',
      'color:#eafff2',
      'outline:none',
      'z-index:1001',
      'font:600 15px system-ui',
    ].join(';');
    input.onfocus = () => (input.style.borderColor = '#f5b23e');
    input.onblur = () => {
      input.style.borderColor = 'rgba(52,211,153,0.4)';
      setTimeout(() => this.#hideSuggest(), 150); // let a suggestion click register before hiding
    };
    input.onkeydown = (e) => {
      if (e.key === 'Enter') {
        this.#hideSuggest();
        void this.#add();
      }
    };
    // Live username autocomplete (debounced) — suggestions drop down under the box.
    input.oninput = () => {
      clearTimeout(this.#suggestTimer);
      const q = input.value.trim();
      if (q.length < 2) {
        this.#hideSuggest();
        return;
      }
      this.#suggestTimer = setTimeout(() => void this.#runSearch(q, x, y, w), 220);
    };
    document.body.appendChild(input);
    this.#addInput = input;
    this.#domEls.push(input);
  }

  /** Absolutely position a DOM element over the Phaser canvas (game-space → screen-space). */
  #positionDom(el, x, y, w, h) {
    const canvas = this.game.canvas;
    const rect = canvas.getBoundingClientRect();
    const sx = rect.width / this.scale.width;
    const sy = rect.height / this.scale.height;
    el.style.position = 'fixed';
    el.style.left = `${rect.left + x * sx}px`;
    el.style.top = `${rect.top + y * sy}px`;
    el.style.width = `${w * sx}px`;
    el.style.height = `${h * sy}px`;
  }

  /** Run a debounced username search and render the suggestion dropdown. */
  async #runSearch(q, x, y, w) {
    const self = netManager?.wallet ?? null;
    const results = await searchPlayers(q, self);
    // Drop stale results (the input changed while the request was in flight).
    if (!this.#addInput || this.#addInput.value.trim() !== q) return;
    this.#renderSuggest(results, x, y, w);
  }

  /** Render clickable username suggestions under the add-friend input. */
  #renderSuggest(results, x, y, w) {
    this.#hideSuggest();
    if (!results.length) return;
    const box = document.createElement('div');
    this.#positionDom(box, x, y + 46, w, 10);
    box.style.height = 'auto';
    box.style.maxHeight = '184px';
    box.style.overflowY = 'auto';
    box.style.background = 'rgba(8,18,12,0.98)';
    box.style.border = '1px solid rgba(52,211,153,0.4)';
    box.style.borderRadius = '10px';
    box.style.zIndex = '1002';
    box.style.boxShadow = '0 8px 24px rgba(0,0,0,0.5)';
    for (const r of results) {
      const row = document.createElement('div');
      row.textContent = r.username;
      row.style.cssText =
        'padding:10px 14px;color:#eafff2;font:600 14px system-ui;cursor:pointer;border-bottom:1px solid rgba(255,255,255,0.06)';
      row.onmouseenter = () => (row.style.background = 'rgba(52,211,153,0.16)');
      row.onmouseleave = () => (row.style.background = 'transparent');
      // mousedown (not click) so it fires BEFORE the input's blur would hide the box.
      row.onmousedown = (e) => {
        e.preventDefault();
        if (this.#addInput) this.#addInput.value = r.username;
        this.#hideSuggest();
        void this.#add();
      };
      box.appendChild(row);
    }
    document.body.appendChild(box);
    this.#suggestBox = box;
    this.#domEls.push(box);
  }

  /** Remove the suggestion dropdown if shown. */
  #hideSuggest() {
    clearTimeout(this.#suggestTimer);
    if (this.#suggestBox) {
      this.#suggestBox.remove();
      this.#suggestBox = undefined;
    }
  }

  async #load() {
    const wallet = netManager?.wallet ?? null;
    this.#view = await fetchFriends(wallet);
    this.#render();
  }

  #render() {
    this.#rowsContainer.removeAll(true);
    const { friends, incoming } = this.#view;

    if (!friends.length && !incoming.length) {
      this.#statusText.setText(
        netManager?.wallet ? 'No friends yet — invite some!' : 'Connect your wallet to add friends',
      );
      return;
    }
    this.#statusText.setText('');

    const x = this.#panelLeft + 30;
    const w = this.#panelW - 60;
    const rowH = 50;
    const gap = 8;
    let y = this.#rowsTop;

    const sectionLabel = (text) => {
      this.#rowsContainer.add(
        this.add
          .text(x + 2, y, text, {
            fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
            fontSize: '13px',
            color: UI.goldHex,
          })
          .setOrigin(0, 0.5),
      );
      y += 24;
    };

    // ── Incoming requests (Accept / decline) first — they're the call to action. ──
    if (incoming.length) {
      sectionLabel(`REQUESTS · ${incoming.length}`);
      for (const req of incoming) {
        if (y + rowH > this.#rowsBottom) break;
        this.#requestRow(req, x, y, w, rowH);
        y += rowH + gap;
      }
    }

    // ── Friends (online dot + name + unfriend). ──
    if (friends.length && y + rowH <= this.#rowsBottom) {
      sectionLabel(`FRIENDS · ${friends.length}`);
      let shown = 0;
      for (const f of friends) {
        if (y + rowH > this.#rowsBottom) break;
        this.#friendRow(f, x, y, w, rowH);
        y += rowH + gap;
        shown += 1;
      }
      // Don't silently drop friends that overflow the panel — tell the player there are more.
      const hidden = friends.length - shown;
      if (hidden > 0) {
        this.#rowsContainer.add(
          this.add
            .text(x + w / 2, y, `+${hidden} more friend${hidden > 1 ? 's' : ''}`, {
              fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
              fontSize: '14px',
              color: UI.subHex,
            })
            .setOrigin(0.5, 0),
        );
      }
    }
  }

  /** @param {import('../net/api.js').IncomingRequest} req */
  #requestRow(req, x, y, w, rowH) {
    const card = this.add.graphics();
    card.fillStyle(0x1c1605, 0.9);
    card.fillRoundedRect(x, y, w, rowH, 10);
    card.lineStyle(2, UI.gold, 0.85);
    card.strokeRoundedRect(x, y, w, rowH, 10);
    this.#rowsContainer.add(card);

    this.#rowsContainer.add(
      this.add
        .text(x + 18, y + rowH / 2, req.username || 'Tamer', {
          fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
          fontSize: '20px',
          color: UI.textHex,
        })
        .setOrigin(0, 0.5),
    );

    this.#rowsContainer.add(
      makeButton(this, {
        x: x + w - 70,
        y: y + rowH / 2,
        width: 96,
        height: 36,
        label: 'Accept',
        primary: true,
        fontSize: 16,
        onClick: () => void this.#accept(req.wallet),
      }),
    );
  }

  /** @param {import('../net/api.js').FriendEntry} f */
  #friendRow(f, x, y, w, rowH) {
    const card = this.add.graphics();
    card.fillStyle(0x0d1c14, 0.85);
    card.fillRoundedRect(x, y, w, rowH, 10);
    card.lineStyle(2, f.online ? ONLINE : UI.glassStroke, f.online ? 0.9 : 0.5);
    card.strokeRoundedRect(x, y, w, rowH, 10);
    this.#rowsContainer.add(card);

    // presence dot
    const dot = this.add.graphics();
    dot.fillStyle(f.online ? ONLINE : OFFLINE_DOT, 1);
    dot.fillCircle(x + 24, y + rowH / 2, 7);
    this.#rowsContainer.add(dot);

    // The name is tappable → opens a profile popup (PFP + level + battle/PvP record + collectibles).
    const nameText = this.add
      .text(x + 44, y + rowH / 2, f.username || 'Tamer', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '20px',
        color: UI.textHex,
      })
      .setOrigin(0, 0.5)
      .setInteractive({ useHandCursor: true });
    nameText.on(Phaser.Input.Events.POINTER_DOWN, () => void this.#showProfile(f));
    this.#rowsContainer.add(nameText);

    if (f.online) {
      // Online → a tappable "JOIN" that teleports you to the friend's current realm.
      const join = this.add
        .text(x + w - 96, y + rowH / 2, '▶ JOIN', {
          fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
          fontSize: '15px',
          color: UI.goldHex,
        })
        .setOrigin(1, 0.5)
        .setInteractive({ useHandCursor: true });
      join.on(Phaser.Input.Events.POINTER_DOWN, () => this.#joinFriend(f));
      this.#rowsContainer.add(join);
    } else {
      this.#rowsContainer.add(
        this.add
          .text(x + w - 96, y + rowH / 2, 'offline', {
            fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
            fontSize: '13px',
            color: UI.subHex,
          })
          .setOrigin(1, 0.5),
      );
    }

    // unfriend ✕
    this.#rowsContainer.add(makeCloseButton(this, x + w - 26, y + rowH / 2, () => void this.#remove(f.wallet)));
  }

  // ── Actions ──────────────────────────────────────────────────────────────────────────────

  async #add() {
    if (this.#busy) return;
    const target = (this.#addInput?.value ?? '').trim();
    if (!target) return;
    this.#busy = true;
    const res = await sendFriendRequest(netManager?.wallet ?? null, target);
    this.#busy = false;
    if (!res.ok) {
      this.#toast(res.error, true);
      return;
    }
    if (this.#addInput) this.#addInput.value = '';
    this.#toast('Request sent!');
    await this.#load();
  }

  async #accept(requester) {
    if (this.#busy) return;
    this.#busy = true;
    const res = await acceptFriend(netManager?.wallet ?? null, requester);
    this.#busy = false;
    if (!res.ok) {
      this.#toast(res.error, true);
      return;
    }
    this.#toast('Friend added!');
    await this.#load();
  }

  async #remove(other) {
    if (this.#busy) return;
    this.#busy = true;
    const res = await apiRemoveFriend(netManager?.wallet ?? null, other);
    this.#busy = false;
    if (!res.ok) {
      this.#toast(res.error, true);
      return;
    }
    await this.#load();
  }

  /** Copy my invite link to the clipboard and toast. Falls back to a select-and-copy if blocked. */
  /** Dismiss the profile popup if open. */
  #closeProfile() {
    this.#profileContainer?.destroy(true);
    this.#profileContainer = undefined;
  }

  /**
   * Open a profile popup for a friend: PFP, level, lifetime battle + PvP record, collectibles, online
   * status, and a Join button if they're online. Tapping the dim backdrop or ✕ closes it.
   * @param {import('../net/api.js').FriendEntry} f
   */
  async #showProfile(f) {
    this.#closeProfile();
    const W = this.scale.width;
    const H = this.scale.height;
    const cx = W / 2;
    const cardW = Math.min(460, W - 50);
    const cardH = 410;
    const cy = H / 2;
    const top = cy - cardH / 2;

    const c = this.add.container(0, 0).setDepth(60);
    this.#profileContainer = c;

    const dim = this.add.rectangle(0, 0, W, H, 0x05080d, 0.8).setOrigin(0).setInteractive();
    dim.on(Phaser.Input.Events.POINTER_DOWN, () => this.#closeProfile());
    c.add(dim);

    const card = this.add.graphics();
    card.fillStyle(0x0d1c14, 0.99);
    card.fillRoundedRect(cx - cardW / 2, top, cardW, cardH, 16);
    card.lineStyle(2, UI.glassStroke, 0.9);
    card.strokeRoundedRect(cx - cardW / 2, top, cardW, cardH, 16);
    c.add(card);

    const loading = this.add
      .text(cx, cy, 'Loading…', { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '18px', color: UI.subHex })
      .setOrigin(0.5);
    c.add(loading);

    const p = await getPublicProfile(f.wallet);
    if (this.#profileContainer !== c || !c.scene) return; // closed/replaced while loading
    loading.destroy();
    if (!p) {
      c.add(
        this.add
          .text(cx, cy, 'Could not load profile', {
            fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
            fontSize: '16px',
            color: UI.subHex,
          })
          .setOrigin(0.5),
      );
      c.add(makeCloseButton(this, cx + cardW / 2 - 24, top + 24, () => this.#closeProfile()));
      return;
    }

    // Avatar: the player's initial in a tinted ring (green when online).
    const avY = top + 80;
    const ring = this.add.graphics();
    ring.fillStyle(p.online ? ONLINE : 0x1b3a2a, 1);
    ring.fillCircle(cx, avY, 46);
    c.add(ring);
    c.add(
      this.add
        .text(cx, avY, (p.username[0] || '?').toUpperCase(), {
          fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
          fontSize: '44px',
          color: '#03210f',
        })
        .setOrigin(0.5),
    );

    c.add(
      this.add
        .text(cx, avY + 70, p.username, {
          fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
          fontSize: '26px',
          color: UI.textHex,
        })
        .setOrigin(0.5),
    );
    const realmLabel = { town: 'Town', wilds: 'The Wilds' }[p.realm ?? ''] ?? '';
    c.add(
      this.add
        .text(cx, avY + 98, p.online ? `● ONLINE${realmLabel ? ` · ${realmLabel}` : ''}` : '○ offline', {
          fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
          fontSize: '14px',
          color: p.online ? UI.emeraldHex : UI.subHex,
        })
        .setOrigin(0.5),
    );

    // Stat rows (label left, value right).
    const rows = [
      ['Level', String(p.level)],
      ['Monsters', String(p.monstersCount)],
      ['Battles', `${p.battlesWon}W · ${p.battlesLost}L`],
      ['PvP', `${p.pvpWon}W · ${p.pvpLost}L`],
      ['Gold', p.gold.toLocaleString()],
    ];
    const rowL = cx - cardW / 2 + 38;
    const rowR = cx + cardW / 2 - 38;
    let ry = avY + 132;
    for (const [label, value] of rows) {
      c.add(
        this.add
          .text(rowL, ry, label, {
            fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
            fontSize: '17px',
            color: UI.subHex,
          })
          .setOrigin(0, 0.5),
      );
      c.add(
        this.add
          .text(rowR, ry, value, {
            fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
            fontSize: '18px',
            color: UI.goldHex,
          })
          .setOrigin(1, 0.5),
      );
      ry += 30;
    }

    c.add(makeCloseButton(this, cx + cardW / 2 - 24, top + 24, () => this.#closeProfile()));

    if (p.online) {
      const by = top + cardH - 34;
      c.add(
        makeButton(this, {
          x: cx - 96,
          y: by,
          width: 176,
          height: 42,
          label: '▶ Join',
          primary: true,
          fontSize: 18,
          onClick: () => {
            this.#closeProfile();
            this.#joinFriend(f);
          },
        }),
      );
      c.add(
        makeButton(this, {
          x: cx + 96,
          y: by,
          width: 176,
          height: 42,
          label: '⚔️ Challenge',
          fontSize: 18,
          onClick: () => {
            const world = this.scene.get(SCENE_KEYS.WORLD_SCENE);
            this.#closeProfile();
            this.#close();
            world?.challengeFriendByName?.(f.username);
          },
        }),
      );
    }
  }

  /**
   * Teleport to an online friend's current realm ("spawn in the same area as them"). No-ops cleanly
   * if their realm has no overworld map (e.g. an arena) or the world scene isn't available.
   * @param {import('../net/api.js').FriendEntry} f
   */
  #joinFriend(f) {
    if (!f?.online) return;
    const REALM_TO_AREA = { town: 'main_1', wilds: 'overworld_1' };
    const area = REALM_TO_AREA[f.realm ?? ''];
    if (!area) {
      this.#toast(`Can't join ${f.username || 'them'} there yet`, true);
      return;
    }
    const world = this.scene.get(SCENE_KEYS.WORLD_SCENE);
    if (!world || typeof world.travelToArea !== 'function') {
      this.#toast('Can’t join right now', true);
      return;
    }
    this.#toast(`Joining ${f.username || 'friend'}…`);
    this.#close();
    world.travelToArea(area);
  }

  #copyInvite() {
    const wallet = netManager?.wallet ?? null;
    if (!wallet) {
      this.#toast('Connect your wallet first', true);
      return;
    }
    const link = `https://monstara.fun/?ref=${wallet}`;
    const done = () => this.#toast('Invite link copied!');
    // Mobile (incl. in-app browsers like Telegram) usually BLOCKS clipboard writes — use the native
    // share sheet there so "Invite Friends" actually works. Falls through to copy on desktop.
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      navigator
        .share({
          title: 'Monstara',
          text: 'Play Monstara with me — catch monsters, earn Gold, win REAL graded Pokémon cards! 🎴',
          url: link,
        })
        .then(() => this.#toast('Invite shared!'))
        .catch(() => {
          /* user cancelled the share sheet — no error toast */
        });
      return;
    }
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(link).then(done).catch(() => this.#fallbackCopy(link, done));
      } else {
        this.#fallbackCopy(link, done);
      }
    } catch {
      this.#fallbackCopy(link, done);
    }
  }

  /** Clipboard fallback for browsers/contexts that block the async API. */
  #fallbackCopy(text, onDone) {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;opacity:0;';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
      onDone();
    } catch {
      this.#toast('Copy failed — link in console', true);
      console.log('[friends] invite link:', text);
    }
  }

  /** A short floating toast near the panel bottom (auto-fades). */
  #toast(message, isError = false) {
    const { width, height } = this.scale;
    const t = this.add
      .text(width / 2, height - 70, message, {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '18px',
        color: isError ? '#ff9a9a' : '#03210f',
        backgroundColor: isError ? '#3a1414' : '#34d399',
        padding: { x: 16, y: 10 },
      })
      .setOrigin(0.5)
      .setDepth(2000);
    this.tweens.add({
      targets: t,
      alpha: 0,
      delay: 1400,
      duration: 500,
      onComplete: () => t.destroy(),
    });
  }

  #destroyDom() {
    clearTimeout(this.#suggestTimer); // cancel a pending search so it can't append a box post-shutdown
    this.#suggestBox = undefined;
    this.#domEls.forEach((e) => e.remove());
    this.#domEls = [];
    this.#addInput = undefined;
  }

  update() {
    super.update();
    if (this._controls?.wasBackKeyPressed?.()) {
      this.#close();
    }
  }

  #close() {
    const prev = this.#sceneData?.previousSceneName ?? SCENE_KEYS.WORLD_SCENE;
    this.scene.stop(SCENE_KEYS.FRIENDS_SCENE);
    if (this.scene.isPaused(prev)) {
      this.scene.resume(prev);
    } else if (this.scene.isSleeping(prev)) {
      // A slept previous scene is neither paused nor inactive: without this branch it would be
      // restarted (heavy) and handleSceneResume would never fire, leaving the player frozen.
      this.scene.wake(prev);
      this.scene.bringToTop(prev);
    } else if (!this.scene.isActive(prev)) {
      this.scene.start(prev);
    }
    // ALWAYS release input — handleSceneResume only fires on resume(), so the wake/start/no-op
    // paths must clear lockInput themselves or the player stays locked.
    const prevScene = this.scene.get(prev);
    if (prevScene?._controls) prevScene._controls.lockInput = false;
  }
}
